// Pure logic for the formula model: composition resolution, scaling, version
// comparison and cost state. No Supabase here — these are the calculations
// the UI and any future export share, and they're the part worth testing.

export const PHASES = ['Phase A', 'Phase B', 'Phase C', 'Cool Down', 'Unassigned'];

/* What each phase means, shown as a key wherever phases are assigned or read.
 *
 * These follow the most common cosmetic convention (A water, B oil, C after
 * the emulsion, cool down for anything heat-sensitive). Conventions do vary
 * between formulators, so this is one place to edit rather than wording
 * repeated across the editor and the batch sheet.
 *
 * "Unassigned" is a real answer, not a gap: an anhydrous product — a balm, a
 * body oil, a sugar scrub — has no water phase to separate from, so there is
 * nothing for phases to describe. */
export const PHASE_GUIDE = [
  {
    name: 'Phase A',
    summary: 'Water phase',
    detail: 'Water, hydrosols, glycerin — anything water-soluble. Heated.',
  },
  {
    name: 'Phase B',
    summary: 'Oil phase',
    detail: 'Butters, oils, waxes and the emulsifier. Heated separately to the same temperature as A.',
  },
  {
    name: 'Phase C',
    summary: 'After the emulsion',
    detail: 'Added once A and B are blended and still warm — thickeners, gums, anything that needs the heat.',
  },
  {
    name: 'Cool Down',
    summary: 'Below about 45 °C',
    detail: 'Preservative, fragrance, actives, vitamin E. Heat would damage or drive these off.',
  },
  {
    name: 'Unassigned',
    summary: 'No phases needed',
    detail: 'Anhydrous products — balms, body oils, scrubs — have no water phase to keep apart, so everything melts and mixes together.',
  },
];

// ---------------------------------------------------------------- costing

// The library price wins over anything stored on the formula line.
//
// This is what makes the shared library worth having: price "Body Cream
// Base" once and every formula using it reprices. `cost_per_kg` on the line
// is a legacy per-formula value from before the library existed, kept only
// as a fallback so nothing loses a price it already had.
export function costPerKg(line) {
  const fromLibrary = Number(line?.ingredient?.cost_per_kg);
  if (fromLibrary > 0) return fromLibrary;
  return Number(line?.cost_per_kg) || 0;
}

export const COST_STATE = {
  COMPLETE: 'complete',
  PARTIAL: 'partial',
  UNAVAILABLE: 'unavailable',
};

// A formula's cost is only meaningful if its ingredients are priced. Showing
// J$0.00 for an unpriced formula reads as "this costs nothing" rather than
// "we don't know", which is worse than showing nothing at all.
export function costState(lines) {
  if (!lines.length) return COST_STATE.UNAVAILABLE;
  const priced = lines.filter((l) => costPerKg(l) > 0);
  if (priced.length === 0) return COST_STATE.UNAVAILABLE;
  if (priced.length < lines.length) return COST_STATE.PARTIAL;
  return COST_STATE.COMPLETE;
}

export function unpricedCount(lines) {
  return lines.filter((l) => !(costPerKg(l) > 0)).length;
}

// ---------------------------------------------------------------- scaling
// ingredientWeight = targetBatchWeight × percentage / 100.
// Scaling is a view of the master formulation, never a new version — you can
// ask "what does 5kg look like" without touching the formula.
export function scaleToBatch(lines, targetBatchG) {
  return lines.map((line) => ({
    ...line,
    grams: (Number(targetBatchG || 0) * Number(line.percentage || 0)) / 100,
  }));
}

export function lineCost(line, targetBatchG) {
  const grams = (Number(targetBatchG || 0) * Number(line.percentage || 0)) / 100;
  return (grams / 1000) * costPerKg(line);
}

export function totalCost(lines, targetBatchG) {
  return lines.reduce((sum, l) => sum + lineCost(l, targetBatchG), 0);
}

export function costPerUnit(total, units) {
  if (!units || units <= 0) return null;
  return total / units;
}

// ------------------------------------------------------------ percentages
export function sumPercentage(lines) {
  return lines.reduce((sum, l) => sum + Number(l.percentage || 0), 0);
}

// A formulation that doesn't total 100% is either mis-entered or incomplete;
// either way the scaled weights will be wrong, so it's surfaced rather than
// silently normalised.
export function percentageStatus(lines) {
  const total = sumPercentage(lines);
  const diff = Number((total - 100).toFixed(4));
  if (Math.abs(diff) < 0.01) return { total, diff: 0, ok: true, direction: null };
  return { total, diff, ok: false, direction: diff > 0 ? 'over' : 'under' };
}

// ------------------------------------------------------------- resolution
// A variant is `base_percentage` of its base plus its own additions, so the
// two together still total 100%. Because the variant resolves against the
// base's CURRENT version, improving a base propagates to every variant —
// which is exactly why versions are immutable and why a batch records the
// base version it actually used.
export function resolveComposition({ ownLines, baseLines, basePercentage }) {
  const share = Number(basePercentage || 0);
  if (!baseLines?.length || !share) {
    return (ownLines || []).map((l) => ({ ...l, source: 'own' }));
  }

  const scaledBase = baseLines.map((l) => ({
    ...l,
    percentage: (Number(l.percentage || 0) * share) / 100,
    source: 'base',
  }));

  return [...scaledBase, ...(ownLines || []).map((l) => ({ ...l, source: 'own' }))];
}

// ------------------------------------------------------------- comparison
// Neither version is modified — this only reports the difference.
export function compareVersions(fromLines, toLines) {
  const key = (l) => (l.ingredient_id || l.ingredient_name || '').toString().toLowerCase();
  const fromMap = new Map(fromLines.map((l) => [key(l), l]));
  const toMap = new Map(toLines.map((l) => [key(l), l]));

  const rows = [];

  toMap.forEach((line, k) => {
    const before = fromMap.get(k);
    if (!before) {
      rows.push({ change: 'added', name: line.ingredient_name, from: null, to: Number(line.percentage) });
      return;
    }
    const a = Number(before.percentage);
    const b = Number(line.percentage);
    if (Math.abs(a - b) < 0.0001) {
      rows.push({ change: 'unchanged', name: line.ingredient_name, from: a, to: b });
    } else {
      rows.push({ change: b > a ? 'increased' : 'decreased', name: line.ingredient_name, from: a, to: b });
    }
  });

  fromMap.forEach((line, k) => {
    if (!toMap.has(k)) {
      rows.push({ change: 'removed', name: line.ingredient_name, from: Number(line.percentage), to: null });
    }
  });

  const order = { added: 0, removed: 1, increased: 2, decreased: 3, unchanged: 4 };
  return rows.sort((x, y) => order[x.change] - order[y.change] || x.name.localeCompare(y.name));
}

// ------------------------------------------------------------------ yield
export function actualYieldPercent(actualFinishedWeightG, targetBatchG) {
  if (!targetBatchG || !actualFinishedWeightG) return null;
  return (Number(actualFinishedWeightG) / Number(targetBatchG)) * 100;
}

// ------------------------------------------------------------------ misc
export function groupByPhase(lines) {
  const groups = new Map();
  lines.forEach((line) => {
    const phase = line.phase || 'Unassigned';
    groups.set(phase, [...(groups.get(phase) || []), line]);
  });
  // Keep the declared phase order; anything unexpected falls to the end.
  return [...groups.entries()].sort(
    (a, b) =>
      (PHASES.indexOf(a[0]) === -1 ? 99 : PHASES.indexOf(a[0])) -
      (PHASES.indexOf(b[0]) === -1 ? 99 : PHASES.indexOf(b[0]))
  );
}
